import { useRef, useState } from 'react'

import { Alert, Box, Button, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import { Form } from 'factorial-form'
import { ContactableApplicationFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SendMessageButton } from 'modules/ats/components/SendMessageButton'
import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import messages from 'modules/ats/store/collections/messages'

class SendEmailForm extends Form {
  constructor(values: { content: string; sendAsCorporateEmail: boolean }) {
    super(values, {
      content: 'string',
      sendAsCorporateEmail: 'boolean',
    })
  }

  get isValid() {
    return !!this.get('content').value
  }
}

interface Props {
  selectedApplications: ContactableApplicationFragment[]
  onClose: () => void
  onSuccess: () => void
}

export const SendEmailToApplicationsModal = observer(
  ({ selectedApplications, onClose, onSuccess }: Props) => {
    const [submitting, setSubmitting] = useState(false)
    const richTextEditorRef = useRef<RichTextEditorHandle>(null)
    const [form] = useState(
      new SendEmailForm({
        content: '',
        sendAsCorporateEmail: false,
      })
    )
    const applicationsWithoutEmail = selectedApplications.filter(
      (application) => !application.candidate?.email
    ).length

    const bulkCreateMessages = async (delayedUntil?: string) => {
      setSubmitting(true)
      await messages.bulkCreate({
        job_application_ids: selectedApplications.map((application) => application.id),
        content: form.get('content').value,
        send_as_corporate_email: form.get('sendAsCorporateEmail').value,
        delayed_until: delayedUntil,
      })

      onSuccess()
      onClose()

      app.setFlash({
        type: 'correct',
        message: !delayedUntil
          ? i18n.tp('ats.candidates.applications.bulk_send_email.modal.sent_successfully', {
              count: selectedApplications.length,
            })
          : i18n.tp('ats.candidates.applications.bulk_send_email.modal.delayed_sent_successfully', {
              count: selectedApplications.length,
            }),
      })
      setSubmitting(false)
    }

    return (
      <Modal
        illustration="user-move"
        title={i18n.tp('ats.candidates.applications.bulk_send_email.modal.title', {
          count: selectedApplications.length,
        })}
        onClose={onClose}
        // TODO: The split button is not a submit button, this form was never submitted
        onSubmit={() => {}}
        footer={
          <>
            <Button type="secondary" onClick={() => onClose()}>
              {i18n.t('common.cancel')}
            </Button>
            <SendMessageButton
              label={i18n.t('ats.candidates.applications.bulk_send_email.modal.send')}
              disabled={submitting || !form.isValid}
              onSendMessage={() => bulkCreateMessages()}
              onSendDelayedMessage={(delayedUntil) => bulkCreateMessages(delayedUntil)}
            />
          </>
        }
      >
        <Box gap="s24">
          <TextEditorWithMessageTemplates
            onChange={(value) => form.setValues({ content: value })}
            placeholder={i18n.t('ats.candidates.applications.bulk_send_email.modal.placeholder')}
            value={form.get('content').value}
            richTextEditorRef={richTextEditorRef}
          />
          <TogglerCheckbox
            value={form.get('sendAsCorporateEmail').value}
            onChange={(sendAsCorporateEmail) => form.setValues({ sendAsCorporateEmail })}
            label={i18n.t('ats.candidates.applications.bulk_send_email.modal.send_as_corporate')}
            information={i18n.t(
              'ats.candidates.applications.bulk_send_email.modal.send_as_corporate_tooltip'
            )}
          />

          {applicationsWithoutEmail > 0 ? (
            <Alert color="warning">
              {i18n.t(
                'ats.candidates.applications.bulk_send_email.modal.applications_without_email',
                {
                  count: applicationsWithoutEmail,
                }
              )}
            </Alert>
          ) : null}
        </Box>
      </Modal>
    )
  }
)
